import * as React from 'react';
import { jsx, css } from '@emotion/react';
import { mediaQueries, autoResponsiveClamp } from 'Src/me/pages/guest/proposals/new/React/viewHelpers';
import type { BaseAnswerType } from '../../../../../../../chat/views/types';
import type { RadioLikeButtonGroupWithImagePropsType } from '../..';
import { Balloon } from '../Balloon';

const PC_IMAGE_SIZE = {
  width: 214,
  height: 168,
};

const { tablet, mobile } = mediaQueries();

type RadioLikeButtonWithImagePropsType = Merge<RadioLikeButtonGroupWithImagePropsType & Required<BaseAnswerType> & {
  imagePaths: {
    imagePath: string;
    webp: string;
  };
  balloonContents?: React.ReactNode;
  isAnswered: boolean;
  onClickHandler: (event: React.MouseEvent<HTMLButtonElement>) => void;
}>;

export const RadioLikeButtonWithImage = ({
  id,
  value,
  textContents,
  gaEventName,
  imagePaths,
  balloonContents,
  isAnswered,
  onClickHandler: onClickHandlerFromProps,
}: RadioLikeButtonWithImagePropsType) => {
  const [isActive, setIsActive] = React.useState<boolean>(null);
  const { imagePath, webp } = imagePaths;
  const onClickHandler = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    // ボタンをアクティブに変更
    setIsActive(true);

    // ボタンのクリックイベントを実行
    onClickHandlerFromProps(event);
  }, [onClickHandlerFromProps]);

  return (
    <div
      css={css`
        position: relative;
      `}
    >
      <button
        type="button"
        id={`ga_event_${gaEventName}_${id}`}
        value={value}
        onClick={onClickHandler}
        css={css`
          color: #041156;
          font-size: 20px;
          position: relative;

          ${mobile(({ clamp }) => css`
            font-size: ${clamp('14px', '18px')};
            font-weight: bold;
          `)}

          ${isAnswered && css`
            pointer-events: none;
          `}
        `}
      >
        <span
          css={css`
            font-size: 0;
            display: block;
            width: 100%;
            margin-bottom: 6px;

            img {
              width: 100%;
              height: auto;
              aspect-ratio: ${PC_IMAGE_SIZE.width} / ${PC_IMAGE_SIZE.height};
            }
          `}
        >
          <picture>
            <source
              srcSet={webp}
              type="image/webp"
            />
            <img
              src={imagePath}
              alt={value}
            />
          </picture>
        </span>
        <span
          css={css`
            display: block;
            padding-left: 40px;
            position: relative;

            ${tablet(({clamp}) => css`
              padding-left: ${clamp('34px', '40px')};
            `)}

            ${mobile(({clamp}) => css`
              padding-left: ${clamp('28px', '34px')};
            `)}

            &::before,
            &::after {
              content: '';
              position: absolute;
            }

            &::before {
              width: 30px;
              height: 30px;
              background-color: #fff;
              border: 1px solid #041156;
              border-radius: 50%;
              top: 0;
              left: 0;

              ${tablet(({clamp}) => css`
                top: 0;
                width: ${clamp('26px', '30px')};
                height: ${clamp('26px', '30px')};
              `)}

              ${mobile(({clamp}) => css`
                width: ${clamp('22px', '26px')};
                height: ${clamp('22px', '26px')};
              `)}
            }

            &::after {
              display: none;
              width: 20px;
              height: 20px;
              background-color: #ffd803;
              border-radius: 50%;
              top: 5px;
              left: 5px;

              ${tablet(({clamp}) => css`
                width: ${clamp('16px', '20px')};
                height: ${clamp('16px', '20px')};
              `)}

              ${mobile(({clamp}) => css`
                width: ${clamp('12px', '16px')};
                height: ${clamp('12px', '16px')};
              `)}

              ${isActive !== null && isActive && css`
                display: block;
              `}
            }
          `}
        >
          {textContents}
        </span>
      </button>
      {balloonContents && <Balloon balloonContents={balloonContents} />}
    </div>
  );
};
